import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, Truck, ArrowDown, ArrowUp, Activity, AlertTriangle, 
  BarChart2, Clock, MapPin, Users, TrendingUp, TrendingDown,
  CheckCircle2, XCircle, Zap, Target, Percent, Boxes
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';

// Mock Data
const MOCK_DATA = {
  warehouses: [
    { id: 1, name: 'Main Warehouse', location: 'Riyadh', capacity: 50000, used: 38500, utilization: 77, status: 'operational', zones: 12, inbound: 1250, outbound: 1180, temperature: 'Ambient', employees: 45 },
    { id: 2, name: 'Distribution Center A', location: 'Jeddah', capacity: 30000, used: 22500, utilization: 75, status: 'operational', zones: 8, inbound: 890, outbound: 850, temperature: 'Ambient', employees: 28 },
    { id: 3, name: 'Cold Storage', location: 'Dammam', capacity: 15000, used: 12000, utilization: 80, status: 'operational', zones: 6, inbound: 450, outbound: 420, temperature: '2-8°C', employees: 15 },
    { id: 4, name: 'Express Hub', location: 'Riyadh', capacity: 20000, used: 18500, utilization: 92, status: 'warning', zones: 10, inbound: 2100, outbound: 2050, temperature: 'Ambient', employees: 35 },
  ],
  dailyMovements: [
    { date: 'Mon', in: 1250, out: 1180, pending: 45 },
    { date: 'Tue', in: 1380, out: 1320, pending: 38 },
    { date: 'Wed', in: 1520, out: 1450, pending: 42 },
    { date: 'Thu', in: 1680, out: 1620, pending: 35 },
    { date: 'Fri', in: 1820, out: 1750, pending: 48 },
    { date: 'Sat', in: 1950, out: 1880, pending: 41 },
    { date: 'Sun', in: 1450, out: 1420, pending: 30 },
  ],
  inventoryByCategory: [
    { name: 'Electronics', value: 12500, color: '#3b82f6' },
    { name: 'Furniture', value: 8900, color: '#10b981' },
    { name: 'Appliances', value: 6700, color: '#f59e0b' },
    { name: 'Office Supplies', value: 5400, color: '#ef4444' },
    { name: 'Raw Materials', value: 12000, color: '#8b5cf6' },
  ],
  recentActivities: [
    { id: 1, type: 'inbound', item: 'Electronics Shipment', quantity: 250, time: '15 min ago', status: 'completed' },
    { id: 2, type: 'outbound', item: 'Order #ORD-2024-089', quantity: 45, time: '1 hour ago', status: 'in-progress' },
    { id: 3, type: 'transfer', item: 'Zone A to Zone C', quantity: 120, time: '2 hours ago', status: 'completed' },
    { id: 4, type: 'inbound', item: 'Furniture Delivery', quantity: 85, time: '3 hours ago', status: 'pending' },
  ],
  alerts: [
    { id: 1, type: 'warning', message: 'Zone A capacity at 92%', priority: 'high' },
    { id: 2, type: 'info', message: '3 shipments arriving today', priority: 'medium' },
    { id: 3, type: 'success', message: 'All pick orders completed', priority: 'low' },
  ],
  performanceMetrics: [
    { metric: 'Pick Accuracy', value: 99.2, target: 99.0, trend: 'up' },
    { metric: 'On-Time Shipment', value: 96.5, target: 95.0, trend: 'up' },
    { metric: 'Inventory Accuracy', value: 98.8, target: 98.0, trend: 'up' },
    { metric: 'Space Utilization', value: 77.3, target: 80.0, trend: 'down' },
  ],
  zoneData: {
    1: [ // Main Warehouse - 12 zones
      { id: 'A1', name: 'Zone A1', capacity: 4500, used: 3200, category: 'Electronics', utilization: 71 },
      { id: 'A2', name: 'Zone A2', capacity: 4500, used: 4100, category: 'Electronics', utilization: 91 },
      { id: 'A3', name: 'Zone A3', capacity: 4500, used: 2800, category: 'Electronics', utilization: 62 },
      { id: 'B1', name: 'Zone B1', capacity: 4200, used: 3500, category: 'Furniture', utilization: 83 },
      { id: 'B2', name: 'Zone B2', capacity: 4200, used: 2900, category: 'Furniture', utilization: 69 },
      { id: 'B3', name: 'Zone B3', capacity: 4200, used: 3100, category: 'Furniture', utilization: 74 },
      { id: 'C1', name: 'Zone C1', capacity: 3800, used: 3200, category: 'Appliances', utilization: 84 },
      { id: 'C2', name: 'Zone C2', capacity: 3800, used: 2800, category: 'Appliances', utilization: 74 },
      { id: 'D1', name: 'Zone D1', capacity: 5000, used: 4200, category: 'Raw Materials', utilization: 84 },
      { id: 'D2', name: 'Zone D2', capacity: 5000, used: 4800, category: 'Raw Materials', utilization: 96 },
      { id: 'E1', name: 'Zone E1', capacity: 3500, used: 2100, category: 'Office Supplies', utilization: 60 },
      { id: 'E2', name: 'Zone E2', capacity: 3500, used: 2700, category: 'Office Supplies', utilization: 77 },
    ],
    2: [ // Distribution Center A - 8 zones
      { id: 'A1', name: 'Zone A1', capacity: 4000, used: 3000, category: 'Electronics', utilization: 75 },
      { id: 'A2', name: 'Zone A2', capacity: 4000, used: 2800, category: 'Electronics', utilization: 70 },
      { id: 'B1', name: 'Zone B1', capacity: 3500, used: 2600, category: 'Furniture', utilization: 74 },
      { id: 'B2', name: 'Zone B2', capacity: 3500, used: 2400, category: 'Furniture', utilization: 69 },
      { id: 'C1', name: 'Zone C1', capacity: 4500, used: 3400, category: 'Appliances', utilization: 76 },
      { id: 'C2', name: 'Zone C2', capacity: 4500, used: 3200, category: 'Appliances', utilization: 71 },
      { id: 'D1', name: 'Zone D1', capacity: 3000, used: 2200, category: 'Office Supplies', utilization: 73 },
      { id: 'D2', name: 'Zone D2', capacity: 3000, used: 2100, category: 'Office Supplies', utilization: 70 },
    ],
    3: [ // Cold Storage - 6 zones
      { id: 'A1', name: 'Zone A1', capacity: 2800, used: 2200, category: 'Frozen Foods', utilization: 79 },
      { id: 'A2', name: 'Zone A2', capacity: 2800, used: 2400, category: 'Frozen Foods', utilization: 86 },
      { id: 'B1', name: 'Zone B1', capacity: 2500, used: 2000, category: 'Dairy', utilization: 80 },
      { id: 'B2', name: 'Zone B2', capacity: 2500, used: 2100, category: 'Dairy', utilization: 84 },
      { id: 'C1', name: 'Zone C1', capacity: 2200, used: 1700, category: 'Beverages', utilization: 77 },
      { id: 'C2', name: 'Zone C2', capacity: 2200, used: 1600, category: 'Beverages', utilization: 73 },
    ],
    4: [ // Express Hub - 10 zones
      { id: 'A1', name: 'Zone A1', capacity: 2200, used: 2100, category: 'Express', utilization: 95 },
      { id: 'A2', name: 'Zone A2', capacity: 2200, used: 2000, category: 'Express', utilization: 91 },
      { id: 'A3', name: 'Zone A3', capacity: 2200, used: 1950, category: 'Express', utilization: 89 },
      { id: 'B1', name: 'Zone B1', capacity: 2000, used: 1900, category: 'Priority', utilization: 95 },
      { id: 'B2', name: 'Zone B2', capacity: 2000, used: 1850, category: 'Priority', utilization: 93 },
      { id: 'C1', name: 'Zone C1', capacity: 1800, used: 1700, category: 'Standard', utilization: 94 },
      { id: 'C2', name: 'Zone C2', capacity: 1800, used: 1650, category: 'Standard', utilization: 92 },
      { id: 'D1', name: 'Zone D1', capacity: 2000, used: 1900, category: 'Returns', utilization: 95 },
      { id: 'D2', name: 'Zone D2', capacity: 2000, used: 1850, category: 'Returns', utilization: 93 },
      { id: 'E1', name: 'Zone E1', capacity: 1600, used: 1500, category: 'Packaging', utilization: 94 },
    ],
  }
};

const WarehouseOverview = () => {
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');

  const metrics = [
    {
      title: "Total Capacity",
      value: "95,000",
      unit: "sq ft",
      change: "+5%",
      trend: "up",
      icon: Boxes,
      color: "blue",
      details: [
        { label: "Used", value: "73,000" },
        { label: "Available", value: "22,000" },
        { label: "Utilization", value: "76.8%" }
      ]
    },
    {
      title: "Inbound Today",
      value: "1,450",
      change: "+12%",
      trend: "up",
      icon: ArrowDown,
      color: "green",
      details: [
        { label: "Received", value: "1,380" },
        { label: "Pending", value: "70" },
        { label: "Avg Time", value: "2.5h" }
      ]
    },
    {
      title: "Outbound Today",
      value: "1,420",
      change: "+8%",
      trend: "up",
      icon: ArrowUp,
      color: "orange",
      details: [
        { label: "Shipped", value: "1,350" },
        { label: "In Process", value: "70" },
        { label: "On-Time", value: "96.5%" }
      ]
    },
    {
      title: "Active Orders",
      value: "234",
      change: "12 urgent",
      trend: "warning",
      icon: Package,
      color: "purple",
      details: [
        { label: "Picking", value: "89" },
        { label: "Packing", value: "45" },
        { label: "Ready", value: "100" }
      ]
    }
  ];

  const quickActions = [
    { icon: ArrowDown, label: "New Receiving", color: "green" },
    { icon: ArrowUp, label: "Create Shipment", color: "orange" },
    { icon: Package, label: "Stock Transfer", color: "blue" },
    { icon: BarChart2, label: "Generate Report", color: "purple" }
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action, index) => (
          <motion.button
            key={index}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`p-4 bg-${action.color}-50 rounded-xl border border-${action.color}-100 flex flex-col items-center gap-2 hover:bg-${action.color}-100/50 transition-colors`}
          >
            <action.icon className={`w-6 h-6 text-${action.color}-600`} />
            <span className={`text-sm font-medium text-${action.color}-700`}>{action.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Warehouse Capacity Visualization */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Warehouse Capacity & Status</h2>
                <p className="text-sm text-gray-500">Real-time capacity utilization across warehouses</p>
              </div>
              <select 
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Warehouses</option>
                {MOCK_DATA.warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {MOCK_DATA.warehouses.map((warehouse) => (
                <div key={warehouse.id} className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{warehouse.name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          warehouse.status === 'operational' ? 'bg-green-100 text-green-700' :
                          warehouse.status === 'warning' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {warehouse.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {warehouse.location}
                        </span>
                        <span>•</span>
                        <span>{warehouse.zones} zones</span>
                        <span>•</span>
                        <span>{warehouse.temperature}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div className="text-center p-2 bg-blue-50 rounded-lg">
                          <div className="text-xs text-gray-600 mb-1">Inbound</div>
                          <div className="text-sm font-bold text-blue-700">{warehouse.inbound}</div>
                        </div>
                        <div className="text-center p-2 bg-orange-50 rounded-lg">
                          <div className="text-xs text-gray-600 mb-1">Outbound</div>
                          <div className="text-sm font-bold text-orange-700">{warehouse.outbound}</div>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded-lg">
                          <div className="text-xs text-gray-600 mb-1">Employees</div>
                          <div className="text-sm font-bold text-purple-700">{warehouse.employees}</div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-gray-900">{warehouse.utilization}%</div>
                      <div className="text-xs text-gray-500">Utilized</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{warehouse.used.toLocaleString()} / {warehouse.capacity.toLocaleString()} sq ft</span>
                      <span className="font-semibold">
                        {warehouse.capacity - warehouse.used > 0 ? `${(warehouse.capacity - warehouse.used).toLocaleString()} available` : 'Full'}
                      </span>
                    </div>
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden relative">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          warehouse.utilization >= 90 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                          warehouse.utilization >= 75 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                          'bg-gradient-to-r from-green-500 to-green-600'
                        }`}
                        style={{ width: `${warehouse.utilization}%` }}
                      />
                      {warehouse.utilization >= 90 && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-white">Warning: Near Capacity</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Recent Activities */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activities</h2>
            <p className="text-sm text-gray-500">Latest warehouse movements</p>
          </div>
          <div className="p-6 space-y-3">
            {MOCK_DATA.recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-lg ${
                  activity.type === 'inbound' ? 'bg-green-100' :
                  activity.type === 'outbound' ? 'bg-orange-100' :
                  'bg-blue-100'
                }`}>
                  {activity.type === 'inbound' ? <ArrowDown className="w-4 h-4 text-green-600" /> :
                   activity.type === 'outbound' ? <ArrowUp className="w-4 h-4 text-orange-600" /> :
                   <Package className="w-4 h-4 text-blue-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{activity.item}</p>
                  <p className="text-xs text-gray-500">Qty: {activity.quantity} • {activity.time}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  activity.status === 'completed' ? 'bg-green-100 text-green-700' :
                  activity.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {activity.status}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Visual Warehouse Zone Map - Impressive Graphical Representation */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Warehouse Zone Capacity Map</h2>
              <p className="text-sm text-gray-600 mt-1">Visual representation of capacity utilization across all zones</p>
            </div>
            <select 
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white font-medium"
            >
              <option value="all">All Warehouses</option>
              {MOCK_DATA.warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="p-6">
          {(selectedWarehouse === 'all' ? MOCK_DATA.warehouses : MOCK_DATA.warehouses.filter(w => w.id.toString() === selectedWarehouse)).map((warehouse) => {
            const zones = MOCK_DATA.zoneData[warehouse.id] || [];
            return (
              <div key={warehouse.id} className="mb-8 last:mb-0">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{warehouse.name}</h3>
                  <span className="text-sm text-gray-600">({warehouse.location})</span>
                  <div className="ml-auto flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-green-500" />
                      <span className="text-gray-600">&lt;75%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-orange-500" />
                      <span className="text-gray-600">75-90%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-500" />
                      <span className="text-gray-600">&gt;90%</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {zones.map((zone) => {
                    const getZoneColor = () => {
                      if (zone.utilization >= 90) return 'from-red-500 to-red-600';
                      if (zone.utilization >= 75) return 'from-orange-500 to-orange-600';
                      return 'from-green-500 to-green-600';
                    };
                    const getBorderColor = () => {
                      if (zone.utilization >= 90) return 'border-red-300';
                      if (zone.utilization >= 75) return 'border-orange-300';
                      return 'border-green-300';
                    };
                    return (
                      <motion.div
                        key={zone.id}
                        whileHover={{ scale: 1.05, zIndex: 10 }}
                        className={`relative bg-gradient-to-br ${getZoneColor()} rounded-lg p-4 border-2 ${getBorderColor()} shadow-md cursor-pointer group`}
                      >
                        <div className="text-white">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-sm">{zone.id}</span>
                            <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded">
                              {zone.utilization}%
                            </span>
                          </div>
                          <div className="text-xs opacity-90 mb-2">{zone.name}</div>
                          <div className="text-xs opacity-75 mb-1">{zone.category}</div>
                          <div className="mt-2 pt-2 border-t border-white/20">
                            <div className="flex justify-between text-xs">
                              <span>{zone.used.toLocaleString()}</span>
                              <span className="opacity-75">/</span>
                              <span>{zone.capacity.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        {/* Capacity Bar */}
                        <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-white rounded-full transition-all"
                            style={{ width: `${zone.utilization}%` }}
                          />
                        </div>
                        {/* Hover Tooltip */}
                        <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-lg shadow-xl p-3 z-20 min-w-[200px] border border-gray-200">
                          <div className="text-xs font-semibold text-gray-900 mb-1">{zone.name}</div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div className="flex justify-between">
                              <span>Category:</span>
                              <span className="font-medium">{zone.category}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Used:</span>
                              <span className="font-medium">{zone.used.toLocaleString()} sq ft</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Capacity:</span>
                              <span className="font-medium">{zone.capacity.toLocaleString()} sq ft</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Available:</span>
                              <span className="font-medium">{(zone.capacity - zone.used).toLocaleString()} sq ft</span>
                            </div>
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <div className="flex justify-between items-center">
                                <span>Utilization:</span>
                                <span className={`font-bold ${
                                  zone.utilization >= 90 ? 'text-red-600' :
                                  zone.utilization >= 75 ? 'text-orange-600' :
                                  'text-green-600'
                                }`}>
                                  {zone.utilization}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                {zones.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No zone data available for this warehouse
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inbound/Outbound Chart */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Movements</h2>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={MOCK_DATA.dailyMovements}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="in" fill="#10b981" fillOpacity={0.3} stroke="#10b981" name="Inbound" />
              <Area type="monotone" dataKey="out" fill="#f59e0b" fillOpacity={0.3} stroke="#f59e0b" name="Outbound" />
              <Line type="monotone" dataKey="pending" stroke="#ef4444" strokeWidth={2} name="Pending" />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Inventory by Category */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Inventory by Category</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={MOCK_DATA.inventoryByCategory}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {MOCK_DATA.inventoryByCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {MOCK_DATA.inventoryByCategory.map((category, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: category.color }} />
                <span className="text-sm text-gray-600">{category.name}</span>
                <span className="text-sm font-semibold text-gray-900 ml-auto">{category.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Capacity Breakdown Visualization */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
      >
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Capacity Distribution Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">Detailed breakdown of warehouse capacity by category and location</p>
        </div>
        <div className="space-y-6">
          {MOCK_DATA.warehouses.map((warehouse) => {
            const zones = MOCK_DATA.zoneData[warehouse.id] || [];
            const categoryBreakdown = zones.reduce((acc, zone) => {
              if (!acc[zone.category]) {
                acc[zone.category] = { total: 0, used: 0, zones: [] };
              }
              acc[zone.category].total += zone.capacity;
              acc[zone.category].used += zone.used;
              acc[zone.category].zones.push(zone);
              return acc;
            }, {});
            
            return (
              <div key={warehouse.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{warehouse.name}</h3>
                  <span className="text-sm text-gray-600">{warehouse.utilization}% overall utilization</span>
                </div>
                <div className="space-y-3">
                  {Object.entries(categoryBreakdown).map(([category, data]) => {
                    const utilization = (data.used / data.total) * 100;
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded ${
                              utilization >= 90 ? 'bg-red-500' :
                              utilization >= 75 ? 'bg-orange-500' :
                              'bg-green-500'
                            }`} />
                            <span className="text-sm font-medium text-gray-700">{category}</span>
                            <span className="text-xs text-gray-500">({data.zones.length} zones)</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-gray-600">{data.used.toLocaleString()} / {data.total.toLocaleString()}</span>
                            <span className={`font-bold ${
                              utilization >= 90 ? 'text-red-600' :
                              utilization >= 75 ? 'text-orange-600' :
                              'text-green-600'
                            }`}>
                              {utilization.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              utilization >= 90 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                              utilization >= 75 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                              'bg-gradient-to-r from-green-500 to-green-600'
                            }`}
                            style={{ width: `${utilization}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Performance Metrics & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Metrics */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h2>
          <div className="space-y-4">
            {MOCK_DATA.performanceMetrics.map((metric, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{metric.metric}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900">{metric.value}%</span>
                    {metric.trend === 'up' ? (
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-orange-600" />
                    )}
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      metric.value >= metric.target ? 'bg-green-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${(metric.value / metric.target) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">Target: {metric.target}%</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Alerts */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Alerts & Notifications</h2>
          <div className="space-y-3">
            {MOCK_DATA.alerts.map((alert) => (
              <div 
                key={alert.id}
                className={`p-3 rounded-lg border-l-4 ${
                  alert.type === 'warning' ? 'bg-orange-50 border-orange-500' :
                  alert.type === 'info' ? 'bg-blue-50 border-blue-500' :
                  'bg-green-50 border-green-500'
                }`}
              >
                <div className="flex items-start gap-2">
                  {alert.type === 'warning' ? <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" /> :
                   alert.type === 'info' ? <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" /> :
                   <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                    <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
                      alert.priority === 'high' ? 'bg-red-100 text-red-700' :
                      alert.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {alert.priority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, unit, change, trend, icon: Icon, color, details }) => {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-${color}-100 rounded-lg`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">
              {value} {unit && <span className="text-sm font-normal text-gray-500">{unit}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center text-green-600">
          {getTrendIcon()}
          <span className="text-sm ml-1">{change}</span>
        </div>
      </div>

      {/* Details */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {details.map((detail, index) => (
          <div key={index} className="text-center">
            <p className="text-xs text-gray-500">{detail.label}</p>
            <p className="text-sm font-medium text-gray-900">{detail.value}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default WarehouseOverview;
